import { Button, Grid, TextField, Autocomplete } from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';

type CreateExecutiveGroupProps = {
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
};

type YearOption = {
  label: string;
  value: string;
};

type FormValues = {
  durationYears: YearOption[]; // we will convert this to "YYYY-YYYY"
};

function CreateExecutiveGroup({
  handleClose,
  setRefreshTrigger,
}: CreateExecutiveGroupProps) {
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      durationYears: [],
    },
  });

  const [loading, setLoading] = useState<boolean>(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  const onSubmit = async (values: FormValues) => {
    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token is missing. Please log in again.',
        });
        return;
      }

      // ✅ validate that at least one year is selected
      if (!values.durationYears || values.durationYears.length === 0) {
        Toast.fire({
          icon: 'warning',
          title: 'Please select at least one year for duration.',
        });
        return;
      }

      setLoading(true);

      // Convert selected years to numeric and build "min-max" range
      const years = values.durationYears
        .map((y) => Number(y.value))
        .filter((y) => !Number.isNaN(y));

      const minYear = Math.min(...years);
      const maxYear = Math.max(...years);

      const duration =
        minYear === maxYear ? `${minYear}` : `${minYear}-${maxYear}`;

      // Send { duration: "2024-2025" } to backend
      const payload = {
        duration,
      };

      const { data } = await axios.post(
        `${url}/api/v1/admin/executive/groups`, // change endpoint if needed
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: `${data.message}`,
        });
        setRefreshTrigger((prev: number) => prev + 1);
        reset();
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: `${data.message}`,
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err?.response?.data?.message || 'Something went wrong'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate year options 2000–2099
  const yearOptions: YearOption[] = Array.from({ length: 100 }, (_, i) => {
    const year = 2000 + i;
    return { label: `${year}`, value: `${year}` };
  });

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Grid container px={1} spacing={1}>
        <Grid item xs={12}>
          <Controller
            control={control}
            name='durationYears'
            render={({ field }) => (
              <Autocomplete
                multiple
                options={yearOptions}
                getOptionLabel={(option) => option.label}
                value={field.value || []}
                onChange={(_, newValue) => {
                  // ensure uniqueness by value
                  const uniqueValues = Array.from(
                    new Set(newValue.map((item) => item.value))
                  ).map((value) =>
                    newValue.find((item) => item.value === value)
                  ) as YearOption[];

                  field.onChange(uniqueValues);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    label='Select years for duration'
                    placeholder='Select one or more years'
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CreateExecutiveGroup;
